// Money is exact decimal strings, end to end.
//
// core-platform stores every amount as numeric(28,10) (ARCHITECTURE.md §4)
// and serializes it as a decimal string (e.g. reporting.go:96-99 "Every
// field is a decimal string straight out of numeric(28,10) — never a
// float"). This module never converts an amount to a JS number: parsing,
// comparison, arithmetic and rounding all run on BigInt at a fixed scale.
//
// Rounding for display is half-even (banker's rounding) to the currency's
// ISO 4217 minor units — the same rule the ledger uses for every split
// (README "round_half_even"). A rounded value is a *display* value only;
// anything sent back to the API is the exact string the user typed or the
// API returned, never a re-serialized rounded figure.

/** Internal fixed scale — matches numeric(28,10). */
export const LEDGER_SCALE = 10;

const DECIMAL_RE = /^([+-])?(\d+)(?:\.(\d+))?$/;

export class InvalidDecimalError extends Error {
  constructor(value: string) {
    super(`Not an exact decimal string: ${JSON.stringify(value)}`);
    this.name = "InvalidDecimalError";
  }
}

export interface ParsedDecimal {
  negative: boolean;
  /** Absolute value scaled by 10^scale. */
  units: bigint;
  scale: number;
}

/** True if `value` is a plain decimal string ("12", "-0.5", "1.2300000000"). */
export function isDecimalString(value: string): boolean {
  return DECIMAL_RE.test(value.trim());
}

/**
 * Parses a decimal string into BigInt units at `scale` fraction digits,
 * rounding half-even if the input has more fraction digits than `scale`.
 */
export function parseDecimal(value: string, scale: number = LEDGER_SCALE): ParsedDecimal {
  const m = DECIMAL_RE.exec(value.trim());
  if (!m) throw new InvalidDecimalError(value);
  const negative = m[1] === "-";
  const intPart = m[2] ?? "0";
  const frac = m[3] ?? "";
  let units: bigint;
  if (frac.length <= scale) {
    units = BigInt(intPart + frac.padEnd(scale, "0"));
  } else {
    const kept = BigInt(intPart + frac.slice(0, scale));
    const rest = frac.slice(scale);
    units = kept + (roundUpHalfEven(kept, rest) ? 1n : 0n);
  }
  return { negative: negative && units !== 0n, units, scale };
}

/** Decides whether dropping `rest` (the discarded digits) rounds `kept` up, half-even. */
function roundUpHalfEven(kept: bigint, rest: string): boolean {
  const first = rest.charCodeAt(0) - 48;
  if (first > 5) return true;
  if (first < 5) return false;
  // Exactly a 5 followed by something: above half if any later digit is non-zero.
  if (/[1-9]/.test(rest.slice(1))) return true;
  // Exactly half: round to even.
  return kept % 2n === 1n;
}

/** Signed BigInt at the ledger scale. */
function toScaled(value: string): bigint {
  const p = parseDecimal(value, LEDGER_SCALE);
  return p.negative ? -p.units : p.units;
}

/** Compares two decimal strings exactly: -1, 0 or 1. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const x = toScaled(a);
  const y = toScaled(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Exact sum of decimal strings, returned as a canonical decimal string. */
export function addDecimal(...values: string[]): string {
  let total = 0n;
  for (const v of values) total += toScaled(v);
  return scaledToString(total, LEDGER_SCALE);
}

/** Exact a - b. */
export function subtractDecimal(a: string, b: string): string {
  return scaledToString(toScaled(a) - toScaled(b), LEDGER_SCALE);
}

export function isZeroDecimal(value: string): boolean {
  return toScaled(value) === 0n;
}

/**
 * floor(budget × multiplier ÷ unitPrice) as a whole-number string, exactly
 * (BigInt, no float): UX-DESIGN.md §2.3 step 3's "up to N" ceiling — CPE
 * is budget ÷ bid, CPM is (budget ÷ bid) × 1,000. Null when the price
 * isn't positive or either value isn't a decimal.
 */
export function maxWholeUnits(budget: string, unitPrice: string, multiplier: number = 1): string | null {
  let b: bigint;
  let p: bigint;
  try {
    b = toScaled(budget);
    p = toScaled(unitPrice);
  } catch {
    return null;
  }
  if (p <= 0n || b < 0n) return null;
  return ((b * BigInt(multiplier)) / p).toString();
}

/** Canonical string (trailing fraction zeros trimmed) of a signed scaled BigInt. */
function scaledToString(signed: bigint, scale: number): string {
  const negative = signed < 0n;
  const abs = negative ? -signed : signed;
  const digits = abs.toString().padStart(scale + 1, "0");
  const intPart = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale).replace(/0+$/, "");
  return `${negative ? "-" : ""}${intPart}${frac ? "." + frac : ""}`;
}

// ---------------------------------------------------------------------------
// Currency minor units

const FALLBACK_MINOR_UNITS: Record<string, number> = {
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0,
  RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
};

/**
 * ISO 4217 minor units for `currency`. Uses the runtime's ICU currency data
 * (Intl) and falls back to a small built-in table; unknown codes get 2.
 */
export function currencyMinorUnits(currency: string): number {
  const code = currency.toUpperCase();
  try {
    const digits = new Intl.NumberFormat("en", { style: "currency", currency: code }).resolvedOptions()
      .maximumFractionDigits;
    if (typeof digits === "number") return digits;
  } catch {
    // Unknown currency code for this ICU build: use the table below.
  }
  return FALLBACK_MINOR_UNITS[code] ?? 2;
}

// ---------------------------------------------------------------------------
// Display formatting

export interface RoundedMoney {
  /** Exact input, untouched. */
  exact: string;
  /** Rounded (half-even) decimal string at the currency's minor units. */
  rounded: string;
  /** True when rounding changed the value, i.e. the display is not exact. */
  isRounded: boolean;
  minorUnits: number;
  negative: boolean;
}

/** Rounds an exact decimal string half-even to the currency's minor units. */
export function roundToMinorUnits(value: string, currency: string): RoundedMoney {
  const minorUnits = currencyMinorUnits(currency);
  const p = parseDecimal(value, minorUnits);
  const signed = p.negative ? -p.units : p.units;
  const rounded = scaledToFixed(signed, minorUnits);
  const isRounded = compareDecimal(value, rounded) !== 0;
  return { exact: value.trim(), rounded, isRounded, minorUnits, negative: p.negative };
}

/** Fixed-point string (always exactly `scale` fraction digits). */
function scaledToFixed(signed: bigint, scale: number): string {
  const negative = signed < 0n;
  const abs = negative ? -signed : signed;
  const digits = abs.toString().padStart(scale + 1, "0");
  const intPart = digits.slice(0, digits.length - scale);
  const frac = digits.slice(digits.length - scale);
  return `${negative ? "-" : ""}${intPart}${scale > 0 ? "." + frac : ""}`;
}

export interface FormatMoneyOptions {
  locale?: string;
  /** "symbol" ($1.00), "code" (USD 1.00). */
  display?: "symbol" | "code";
}

/**
 * The one display formatter for money. Rounds half-even to the currency's
 * minor units, then lays the digits out with the locale's currency pattern.
 *
 * Intl is only used to learn the *pattern* (symbol, separators, sign
 * placement) from the template numbers 1 and -1; the amount's own digits
 * are spliced in as strings, so no money value ever passes through a
 * JS number.
 */
export function formatMoney(value: string, currency: string, opts: FormatMoneyOptions = {}): string {
  const locale = opts.locale ?? "en-US";
  const r = roundToMinorUnits(value, currency);
  const [intDigits, fracDigits = ""] = r.rounded.replace(/^-/, "").split(".");
  let nf: Intl.NumberFormat;
  try {
    nf = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
      currencyDisplay: opts.display === "code" ? "code" : "symbol",
      minimumFractionDigits: r.minorUnits,
      maximumFractionDigits: r.minorUnits,
    });
  } catch {
    return `${r.negative ? "-" : ""}${groupDigits(intDigits ?? "0", ",")}${fracDigits ? "." + fracDigits : ""} ${currency.toUpperCase()}`;
  }
  const groupSep =
    new Intl.NumberFormat(locale).formatToParts(1000).find((p) => p.type === "group")?.value ?? ",";
  const template = nf.formatToParts(r.negative ? -1 : 1);
  let fractionPlaced = false;
  const out = template.map((part) => {
    switch (part.type) {
      case "integer":
        return groupDigits(intDigits ?? "0", groupSep);
      case "fraction":
        fractionPlaced = true;
        return fracDigits;
      default:
        return part.value;
    }
  });
  if (!fractionPlaced && fracDigits) {
    // Template had no fraction part (should not happen with min digits set).
    return out.join("") + "." + fracDigits;
  }
  return out.join("");
}

function groupDigits(digits: string, sep: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

// ---------------------------------------------------------------------------
// Input validation (forms)

export type AmountValidation =
  | { ok: true; value: string }
  | { ok: false; reason: "empty" | "format" | "precision" | "not_positive" };

/**
 * Validates an amount typed into a form. Accepts only plain positive
 * decimals with at most the currency's minor-unit precision; returns the
 * normalized exact string to send to the API.
 */
export function validateAmountInput(raw: string, currency: string): AmountValidation {
  const v = raw.trim().replace(/,/g, "");
  if (v === "") return { ok: false, reason: "empty" };
  if (!/^\d+(\.\d+)?$/.test(v)) return { ok: false, reason: "format" };
  const frac = v.split(".")[1] ?? "";
  if (frac.length > currencyMinorUnits(currency)) return { ok: false, reason: "precision" };
  if (compareDecimal(v, "0") <= 0) return { ok: false, reason: "not_positive" };
  return { ok: true, value: v };
}

export function amountValidationMessage(reason: Exclude<AmountValidation, { ok: true }>["reason"], currency: string): string {
  const mu = currencyMinorUnits(currency);
  switch (reason) {
    case "empty":
      return "Enter an amount.";
    case "format":
      return "Enter a number, like 250 or 250.00.";
    case "precision":
      return mu === 0
        ? `${currency.toUpperCase()} has no minor units — enter a whole number.`
        : `Use at most ${mu} decimal places for ${currency.toUpperCase()}.`;
    case "not_positive":
      return "The amount must be greater than zero.";
  }
}
